import { getPassStatus } from '../../../utils/statusUtils';
import type { PassViewRecord } from './fetchWeldViewApis';
import { parsePassUnixSeconds } from './passViewFormatters';

export interface ProductionPassFilterState {
  passNameFilter: string;
  stationFilter: string;
  bugTypeFilter: string[];
  statusFilter: string[];
  welderIdFilter: string[];
  dateRange: { dateFrom: Date | undefined; dateTo: Date | undefined };
}

export const emptyProductionPassFilters = (): ProductionPassFilterState => ({
  passNameFilter: '',
  stationFilter: '',
  bugTypeFilter: [],
  statusFilter: [],
  welderIdFilter: [],
  dateRange: { dateFrom: undefined, dateTo: undefined },
});

export interface ProductionPassFilterOptions {
  bugTypes: string[];
  statuses: string[];
  welderIds: string[];
  stations: string[];
}

/** Map analytics pass row to fields `getPassStatus` reads from production records. */
function passRecordForStatus(pass: PassViewRecord): Record<string, unknown> {
  return {
    pass_status_by_instantaneous: pass.passStatusInstantaneous ?? pass.status,
    pass_avg_status: pass.passAvgStatus,
    pass_staus_by_zone: pass.passStatusZone,
    statusByInstantaneous: pass.passStatusInstantaneous ?? pass.status,
    statusByAvgPass: pass.passAvgStatus,
  };
}

export function buildPassFilterOptions(
  passes: PassViewRecord[],
  statusCalculationMethod: string,
): ProductionPassFilterOptions {
  const bugTypes = Array.from(
    new Set(passes.map((p) => p.bugType).filter((t): t is string => Boolean(t))),
  ).sort();
  const welderIds = Array.from(
    new Set(passes.map((p) => p.welderId).filter((id) => id && id !== '—')),
  ).sort();
  const stations = Array.from(
    new Set(
      passes
        .map((p) => p.stationNumber?.trim())
        .filter((s): s is string => Boolean(s)),
    ),
  ).sort();
  const statusSet = new Set<string>();
  passes.forEach((pass) => {
    const status = getPassStatus(statusCalculationMethod, passRecordForStatus(pass));
    if (status != null && String(status).trim() !== '') {
      statusSet.add(String(status));
    }
  });
  return {
    bugTypes,
    statuses: Array.from(statusSet).sort(),
    welderIds,
    stations,
  };
}

function passDateInRange(
  pass: PassViewRecord,
  dateRange: ProductionPassFilterState['dateRange'],
): boolean {
  if (!dateRange.dateFrom && !dateRange.dateTo) return true;

  const startMs = parsePassUnixSeconds(pass.startTime);
  const endMs = parsePassUnixSeconds(pass.endTime);
  const passStartDate = startMs != null ? new Date(startMs) : null;
  const passEndDate = endMs != null ? new Date(endMs) : null;

  let hasDateInRange = false;

  if (passStartDate) {
    const startDate = new Date(passStartDate);
    startDate.setHours(0, 0, 0, 0);

    if (dateRange.dateFrom) {
      const fromDate = new Date(dateRange.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (startDate >= fromDate) {
        if (!dateRange.dateTo || startDate <= new Date(dateRange.dateTo)) {
          hasDateInRange = true;
        }
      }
    } else if (dateRange.dateTo) {
      const toDate = new Date(dateRange.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (startDate <= toDate) {
        hasDateInRange = true;
      }
    }
  }

  if (!hasDateInRange && passEndDate) {
    const endDate = new Date(passEndDate);
    endDate.setHours(0, 0, 0, 0);

    if (dateRange.dateFrom) {
      const fromDate = new Date(dateRange.dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      if (endDate >= fromDate) {
        if (!dateRange.dateTo || endDate <= new Date(dateRange.dateTo)) {
          hasDateInRange = true;
        }
      }
    } else if (dateRange.dateTo) {
      const toDate = new Date(dateRange.dateTo);
      toDate.setHours(23, 59, 59, 999);
      if (endDate <= toDate) {
        hasDateInRange = true;
      }
    }
  }

  return hasDateInRange;
}

export function passMatchesProductionFilters(
  pass: PassViewRecord,
  filters: ProductionPassFilterState,
  statusCalculationMethod: string,
): boolean {
  const {
    passNameFilter,
    stationFilter,
    bugTypeFilter,
    statusFilter,
    welderIdFilter,
    dateRange,
  } = filters;

  if (passNameFilter && pass.name) {
    const nameMatch = pass.name.toLowerCase().includes(passNameFilter.toLowerCase());
    if (!nameMatch) return false;
  }

  if (
    stationFilter &&
    pass.stationNumber &&
    !String(pass.stationNumber).toLowerCase().includes(stationFilter.toLowerCase())
  ) {
    return false;
  }

  if (bugTypeFilter.length > 0 && pass.bugType && !bugTypeFilter.includes(pass.bugType)) {
    return false;
  }

  if (statusFilter.length > 0) {
    const status = getPassStatus(statusCalculationMethod, passRecordForStatus(pass));
    if (status && !statusFilter.includes(String(status))) {
      return false;
    }
  }

  if (welderIdFilter.length > 0 && pass.welderId && !welderIdFilter.includes(pass.welderId)) {
    return false;
  }

  if (!passDateInRange(pass, dateRange)) {
    return false;
  }

  return true;
}

export function filterPassViewRecords(
  passes: PassViewRecord[],
  filters: ProductionPassFilterState,
  statusCalculationMethod: string,
): PassViewRecord[] {
  return passes.filter((pass) =>
    passMatchesProductionFilters(pass, filters, statusCalculationMethod),
  );
}
